// src/components/products/ProductTemplate.jsx
import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Link } from 'react-router-dom';

const ProductTemplate = ({ 
  title, 
  badge, 
  tagline,
  description, 
  heroImage, 
  features, 
  benefits, 
  industries,
  specifications,
  ctaText = "Request Demo",
  ctaLink = "/contact",
  productImage
}) => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('features');

  return (
    <div className="pt-20">
      {/* Hero Section with Background Image */}
      <section className={`relative py-16 sm:py-20 lg:py-24 overflow-hidden ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover object-center"
          />
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-r from-black/85 via-black/70 to-black/50' 
              : 'bg-gradient-to-r from-white/85 via-white/70 to-white/50'
          }`} />
        </div>

        {isDark && (
          <>
            <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-700/15 rounded-full blur-3xl z-0" />
            <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-700/15 rounded-full blur-3xl z-0" />
          </>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-4">
                <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block backdrop-blur-sm ${
                  isDark 
                    ? 'text-blue-400 bg-blue-700/20 border border-blue-700/30' 
                    : 'text-blue-700 bg-blue-100 border border-blue-200'
                }`}>
                  {badge}
                </span>
              </div>
              
              <h1 className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {title}
              </h1>
              
              <p className={`text-xl mb-4 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                {tagline}
              </p>
              
              <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                {description}
              </p>
            </div>
            
            <div className="relative">
              <div className={`rounded-2xl overflow-hidden shadow-2xl ${isDark ? 'border border-white/10' : 'border border-gray-200'}`}>
                <img 
                  src={productImage}
                  alt={title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Overview Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {' '}
              <span className="bg-gradient-to-r from-blue-400 to-blue-700 bg-clip-text text-transparent">
                {title.split(' ')[0]}
              </span>
            </h2>
            <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Built for modern workplaces, designed for excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className={`p-8 rounded-2xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="text-4xl mb-4">🎯</div>
              <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Key Benefits
              </h3>
              <ul className="space-y-2">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-700'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className={`p-8 rounded-2xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="text-4xl mb-4">🏭</div>
              <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Industries We Serve
              </h3>
              <div className="flex flex-wrap gap-2">
                {industries.map((industry, index) => (
                  <span key={index} className={`px-3 py-1 rounded-full text-sm ${
                    isDark 
                      ? 'bg-blue-700/20 text-blue-300' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {industry}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Tabs Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-gray-900/30' : 'bg-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Powerful{' '}
              <span className="bg-gradient-to-r from-blue-400 to-blue-700 bg-clip-text text-transparent">
                Features
              </span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Everything you need to manage and streamline your operations
            </p>
          </div>

          {/* Tab Buttons */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setActiveTab('features')}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'features'
                  ? 'bg-blue-700 text-white shadow-lg'
                  : isDark 
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              All Features
            </button>

            <button
              onClick={() => setActiveTab('specs')}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'specs'
                  ? 'bg-blue-700 text-white shadow-lg'
                  : isDark 
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              Technical Specs
            </button>
          </div>

          {/* Features Grid */}
          {activeTab === 'features' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`group p-4 rounded-xl transition-all duration-300 flex items-start gap-3 ${
                    isDark 
                      ? 'bg-white/5 border border-white/10 hover:border-blue-700/30 hover:bg-white/10' 
                      : 'bg-white border border-gray-200 hover:border-blue-400 hover:shadow-md'
                  }`}
                >
                  <div className="text-2xl">{feature.icon}</div>
                  <div>
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {feature.title}
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Specifications */}
          {activeTab === 'specs' && (
            <div className={`rounded-2xl p-8 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {specifications.map((spec, index) => (
                  <div key={index} className="flex justify-between py-3 border-b border-gray-200 dark:border-white/10">
                    <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {spec.label}
                    </span>
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ready to Transform Your Operations?
          </h2>

          <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Schedule a demo and see how {title.split(' ')[0]} can help your business
          </p>

          <Link to={ctaLink}>
            <button className="px-8 py-3 rounded-xl bg-blue-700 text-white font-semibold hover:bg-blue-800 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              {ctaText}
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductTemplate;